import { useEffect, useState } from 'react';
import { Bell, BellOff, Smartphone, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

type Prefs = { stall: boolean; hot: boolean; csi_red: boolean };
const DEFAULT_PREFS: Prefs = { stall: true, hot: true, csi_red: true };

export function NotificationsSettings() {
  const { mode, userId } = useAuth();
  const { supported, permission, subscribed, loading, iosNeedsPwa, subscribe, unsubscribe } = usePushSubscription();
  const [prefs, setPrefs] = useState<Prefs>(DEFAULT_PREFS);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode !== 'real' || !userId) return;
    supabase.from('profiles').select('notification_prefs').eq('id', userId).maybeSingle().then(({ data }) => {
      const p = (data?.notification_prefs ?? {}) as Partial<Prefs>;
      setPrefs({ ...DEFAULT_PREFS, ...p });
    });
  }, [mode, userId]);

  if (mode !== 'real') return null;

  const updatePref = async (key: keyof Prefs, value: boolean) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    if (!userId) return;
    await supabase.from('profiles').update({ notification_prefs: next as never }).eq('id', userId);
  };

  const handleToggle = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await unsubscribe();
        toast.success('Notifications disabled');
      } else {
        const r = await subscribe();
        if (!r.ok) toast.error(r.error ?? 'Could not enable notifications');
        else toast.success('Notifications enabled');
      }
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" /> Push Notifications
        </CardTitle>
        <CardDescription>
          Get pinged when a deal stalls, goes hot, or a customer leaves a red rating.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!supported && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-muted/50 text-sm text-muted-foreground">
            <BellOff className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Push notifications aren't supported in this browser. Try Chrome on desktop or Android.</span>
          </div>
        )}

        {supported && iosNeedsPwa && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-primary/5 border border-primary/20 text-sm">
            <Smartphone className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
            <div>
              <p className="font-medium">iPhone? Add to Home Screen first</p>
              <p className="text-xs text-muted-foreground mt-1">
                Tap the Share button in Safari, then "Add to Home Screen". Open PullOver from the home screen icon, then come back here to enable notifications.
              </p>
            </div>
          </div>
        )}

        {supported && !iosNeedsPwa && (
          <>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Enable on this device</p>
                <p className="text-xs text-muted-foreground">
                  {subscribed ? 'You\u2019ll get pushes here.' : permission === 'denied' ? 'Blocked in browser settings — allow notifications then try again.' : 'One tap to subscribe this device.'}
                </p>
              </div>
              <Button size="sm" onClick={handleToggle} disabled={busy || loading || permission === 'denied'} variant={subscribed ? 'outline' : 'default'}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : subscribed ? 'Disable' : 'Enable'}
              </Button>
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Send me a push when…</p>
              <PrefRow label="A deal stalls (24h no activity)" checked={prefs.stall} onChange={v => updatePref('stall', v)} disabled={!subscribed} />
              <PrefRow label="A deal goes Hot (3+ activities in 48h)" checked={prefs.hot} onChange={v => updatePref('hot', v)} disabled={!subscribed} />
              <PrefRow label="A customer leaves a Red CSI rating" checked={prefs.csi_red} onChange={v => updatePref('csi_red', v)} disabled={!subscribed} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PrefRow({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={`text-sm ${disabled ? 'text-muted-foreground' : ''}`}>{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}
